import type { DeviceSpecDevice, DeviceSpecTag, SpecMachine } from '../../types'
import { addressKey } from './pullMerge'

// 마스터데이터 명세 — 태그 찾기·점검·인터페이스 ID 바꿀 때 전송코드 앞부분 따라 바꾸기.

// ── 태그 찾기 ──

export type TagLookup = (device: string, address: string) => { device: DeviceSpecDevice; tag: DeviceSpecTag } | null

/** 디바이스 키 + 주소 → 명세 태그 (주소는 대소문자·띄어쓰기·숫자 앞 0 무시) */
export function tagLookup(devices: DeviceSpecDevice[]): TagLookup {
  const byDevice = new Map(devices.map(d => {
    const tags = new Map<string, DeviceSpecTag>()
    for (const t of d.tags) {
      const k = addressKey(d.channelType, t.address)
      if (k && !tags.has(k)) tags.set(k, t)
    }
    return [d.key, { device: d, tags }]
  }))
  return (device, address) => {
    const d = byDevice.get(device)
    const tag = d && address.trim() ? d.tags.get(addressKey(d.device.channelType, address)) : undefined
    return d && tag ? { device: d.device, tag } : null
  }
}

export const deviceLabel = (d: DeviceSpecDevice) => d.name || d.code || '(이름 없음)'

// ── 점검 ──

/** 겹치는 전송코드가 든 자리 — 인터페이스와 그 항목 */
export interface DupPlace { machineKey: string; machineCode: string; itemKey: string; description: string }

export interface MasterIssues {
  /** 겹치는 전송코드 (명세서 전체) */
  dupCodes: Set<string>
  /** 겹치는 전송코드별 자리 (코드 순) — MES 는 전송코드로 값을 구분하므로 하나만 남겨야 한다 */
  dupPlaces: [string, DupPlace[]][]
  /** 인터페이스별 — 항목 수, 태그 연결 안 됨, 명세에 없는 태그, 겹치는 전송코드인 항목 */
  machine: Record<string, { items: number; unbound: number; missing: number; dup: number }>
}

export function masterIssues(machines: SpecMachine[], lookup: TagLookup): MasterIssues {
  const places = new Map<string, DupPlace[]>()
  const machine: MasterIssues['machine'] = {}
  for (const m of machines) {
    let unbound = 0, missing = 0
    for (const i of m.items) {
      const code = i.code.trim()
      if (code) places.set(code, [...(places.get(code) ?? []), { machineKey: m.key, machineCode: m.code, itemKey: i.key, description: i.description }])
      if (!i.device || !i.address.trim()) unbound++
      else if (!lookup(i.device, i.address)) missing++
    }
    machine[m.key] = { items: m.items.length, unbound, missing, dup: 0 }
  }
  const dupPlaces = [...places].filter(([, ps]) => ps.length > 1).sort(([a], [b]) => a.localeCompare(b))
  for (const [, ps] of dupPlaces) for (const p of ps) machine[p.machineKey].dup++
  return { dupCodes: new Set(dupPlaces.map(([c]) => c)), dupPlaces, machine }
}

/** 태그 Alias 로 만든 전송코드 — {인터페이스 ID}_{Alias} (인터페이스 ID나 Alias 가 비면 '') */
export function mesCode(machineCode: string, lookup: TagLookup, device: string, address: string): string {
  const alias = device && address.trim() ? lookup(device, address)?.tag.alias.trim() : ''
  return alias && machineCode.trim() ? `${machineCode.trim()}_${alias}` : ''
}

/** 전송코드가 빈 항목을 태그 Alias 로 채운다 (설명도 비었으면 태그 설명으로). 바뀐 게 없으면 같은 배열 */
export function fillFromTags(machine: SpecMachine, lookup: TagLookup): SpecMachine['items'] {
  let changed = false
  const items = machine.items.map(i => {
    if (i.code.trim()) return i
    const code = mesCode(machine.code, lookup, i.device, i.address)
    if (!code) return i
    changed = true
    return { ...i, code, description: i.description.trim() ? i.description : lookup(i.device, i.address)?.tag.description ?? '' }
  })
  return changed ? items : machine.items
}

/** 인터페이스 ID 를 바꾸면 그 코드로 시작하던 전송코드 앞부분도 바꾼다 (110_CNT → 111_CNT) */
export function renamePrefix(code: string, from: string, to: string): string {
  const f = from.trim(), t = to.trim()
  return f && t && f !== t && code.startsWith(f) ? t + code.slice(f.length) : code
}
